/**
 * Session helpers shared by the parent query handoff (`applyOAuth2AuthFromQuery`) and the core stores.
 *
 * There are **no cookies** in this deployment: the parent passes the session via URL query and this
 * module only normalises the localStorage/sessionStorage keys the legacy MobX stores read.
 */

export const isEverymanAuthLogFull = (): boolean => {
    try {
        return (
            typeof window !== 'undefined' &&
            (localStorage.getItem('everyman_auth_log_full') === '1' ||
                sessionStorage.getItem('everyman_auth_log_full') === '1')
        );
    } catch {
        return false;
    }
};

/**
 * PU stores `client.accounts` as a JSON array; dtrader MobX expects `accounts[loginid]`.
 * Arrays make `is_logged_in` false and skip `deriv_api.authorize()` on socket open.
 */
export function normalizeClientAccountsShape(accounts: unknown): Record<string, unknown> {
    if (!accounts || typeof accounts !== 'object') return {};
    if (Array.isArray(accounts)) {
        const obj: Record<string, unknown> = {};
        accounts.forEach(row => {
            const r = row as { loginid?: string };
            if (r && typeof r.loginid === 'string') obj[r.loginid] = row;
        });
        return obj;
    }
    return accounts as Record<string, unknown>;
}

/** Rewrite `localStorage['client.accounts']` when it is still an array (e.g. after a REST hydrate). */
export function normalizeClientAccountsInLocalStorage(): boolean {
    try {
        const raw = localStorage.getItem('client.accounts');
        if (!raw) return false;
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return false;
        const norm = normalizeClientAccountsShape(parsed);
        localStorage.setItem('client.accounts', JSON.stringify(norm));
        return true;
    } catch {
        return false;
    }
}

/**
 * Align sessionStorage keys `client.init` reads for the active account (used after the parent query
 * handoff persists the session). Not cookie-related — `sessionStorage` is per-tab scratch state.
 */
export const syncEverymanActiveLoginSessionKeys = (): void => {
    try {
        const id = (localStorage.getItem('active_loginid') || '').trim();
        if (!id) return;
        if (/^(CR|MF|VRTC)/i.test(id)) {
            sessionStorage.setItem('active_loginid', id);
        }
        if (/^(CRW|MFW|VRW)/i.test(id)) {
            sessionStorage.setItem('active_wallet_loginid', id);
        }
        if (/^DOT/i.test(id)) {
            sessionStorage.setItem('active_loginid', id);
        }
    } catch {
        /* ignore */
    }
};
